import React, { useEffect, useMemo, useState } from 'react';
import { PencilSquareIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useEmployees } from '../hooks/useEmployees';
import { useStores } from '../hooks/useStores';
import type { Employee, EmployeeRole } from '../services/employeeService';

type EditForm = {
  first_name: string;
  last_name: string;
  cellphone: string;
};

type AddRoleForm = {
  store_id: string;
  car_model: string;
  number_plate: string;
};

const roleBadgeStyles: Record<EmployeeRole, string> = {
  driver: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  picker: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
};

export default function Employees() {
  const { employees, loading, error: hookError, fetchEmployees, updateEmployeeProfile, addPickerRole, addDriverRole, removePickerRole, removeDriverRole } = useEmployees();
  const { stores, fetchStores } = useStores();

  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState<Employee | null>(null);
  const [editForm, setEditForm] = useState<EditForm>({ first_name: '', last_name: '', cellphone: '' });
  const [submitting, setSubmitting] = useState(false);

  const [addingRoleFor, setAddingRoleFor] = useState<{ employee: Employee; role: EmployeeRole } | null>(null);
  const [addRoleForm, setAddRoleForm] = useState<AddRoleForm>({ store_id: '', car_model: '', number_plate: '' });
  const [addRoleError, setAddRoleError] = useState<string | null>(null);

  const [removingRoleFor, setRemovingRoleFor] = useState<{ employee: Employee; role: EmployeeRole } | null>(null);
  const [removeRoleError, setRemoveRoleError] = useState<string | null>(null);

  useEffect(() => {
    void Promise.all([fetchEmployees(), fetchStores()]);
  }, [fetchEmployees, fetchStores]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter((emp) => {
      const fullName = `${emp.first_name} ${emp.last_name}`.toLowerCase();
      const storeNames = `${emp.driver_store_name ?? ''} ${emp.picker_store_name ?? ''}`.toLowerCase();
      return fullName.includes(q) || storeNames.includes(q);
    });
  }, [employees, query]);

  const openEdit = (employee: Employee) => {
    setEditing(employee);
    setEditForm({
      first_name: employee.first_name,
      last_name: employee.last_name,
      cellphone: employee.cellphone,
    });
  };

  const submitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing || submitting) return;
    setSubmitting(true);
    try {
      const success = await updateEmployeeProfile(editing.auth_id, editForm);
      if (success) setEditing(null);
    } finally {
      setSubmitting(false);
    }
  };

  const openAddRole = (employee: Employee, role: EmployeeRole) => {
    setAddingRoleFor({ employee, role });
    setAddRoleForm({ store_id: '', car_model: '', number_plate: '' });
    setAddRoleError(null);
  };

  const submitAddRole = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addingRoleFor || submitting) return;
    setSubmitting(true);
    setAddRoleError(null);
    try {
      const { employee, role } = addingRoleFor;
      const success = role === 'picker'
        ? await addPickerRole({
            auth_id: employee.auth_id,
            first_name: employee.first_name,
            last_name: employee.last_name,
            store_id: addRoleForm.store_id,
          })
        : await addDriverRole({
            auth_id: employee.auth_id,
            first_name: employee.first_name,
            last_name: employee.last_name,
            store_id: addRoleForm.store_id,
            car_model: addRoleForm.car_model,
            number_plate: addRoleForm.number_plate,
          });

      if (success) {
        setAddingRoleFor(null);
      } else {
        setAddRoleError(`Failed to add ${role} role. Check the details and try again.`);
      }
    } catch (err) {
      setAddRoleError(err instanceof Error ? err.message : 'Failed to add role.');
    } finally {
      setSubmitting(false);
    }
  };

  const openRemoveRole = (employee: Employee, role: EmployeeRole) => {
    setRemovingRoleFor({ employee, role });
    setRemoveRoleError(null);
  };

  const confirmRemoveRole = async () => {
    if (!removingRoleFor || submitting) return;
    setSubmitting(true);
    setRemoveRoleError(null);
    try {
      const { employee, role } = removingRoleFor;
      const success = role === 'picker'
        ? await removePickerRole(employee.picker_id!)
        : await removeDriverRole(employee.driver_id!);
      if (success) {
        setRemovingRoleFor(null);
      } else {
        setRemoveRoleError(hookError ?? `Failed to remove ${role} role.`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-stone-900">Employees</h1>
          <p className="text-sm text-stone-500 mt-1">
            View everyone across driver and picker roles, and grant additional roles to existing staff.
          </p>
        </div>
        <input
          placeholder="Search name or store…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#ee2b74]"
        />
      </header>

      <section className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
        <div className="px-5 py-3 border-b border-stone-200 flex items-center justify-between">
          <h2 className="font-semibold text-stone-900">All Employees</h2>
          <span className="text-sm text-stone-500">{filtered.length} total</span>
        </div>

        {loading ? (
          <div className="px-4 py-10 text-center text-sm text-stone-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-stone-500">
            {query ? `No employees match "${query}"` : 'No employees yet. Add a driver or picker to get started.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Full Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Cellphone</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Roles</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Store(s)</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {filtered.map((emp) => (
                  <tr key={emp.auth_id} className="hover:bg-stone-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-stone-900">
                      {emp.first_name} {emp.last_name}
                    </td>
                    <td className="px-4 py-3 text-stone-600">{emp.cellphone || '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1.5 flex-wrap">
                        {emp.roles.map((role) => (
                          <span
                            key={role}
                            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ring-1 ring-inset ${roleBadgeStyles[role]}`}
                          >
                            {role}
                            <button
                              onClick={() => openRemoveRole(emp, role)}
                              title={`Remove ${role} role`}
                              className="ml-0.5 -mr-1 rounded-full hover:bg-black/10 p-0.5 leading-none"
                            >
                              ✕
                            </button>
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-stone-600 text-xs">
                      {emp.roles.includes('driver') && <div>Driver: {emp.driver_store_name ?? '—'}</div>}
                      {emp.roles.includes('picker') && <div>Picker: {emp.picker_store_name ?? '—'}</div>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => openEdit(emp)}
                          className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 border border-stone-200 bg-white text-xs text-stone-700 hover:bg-stone-50 transition"
                        >
                          <PencilSquareIcon className="h-3.5 w-3.5" />
                          Edit
                        </button>
                        {!emp.roles.includes('picker') && (
                          <button
                            onClick={() => openAddRole(emp, 'picker')}
                            className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 border border-emerald-200 bg-white text-xs text-emerald-700 hover:bg-emerald-50 transition"
                          >
                            <PlusIcon className="h-3.5 w-3.5" />
                            Picker Role
                          </button>
                        )}
                        {!emp.roles.includes('driver') && (
                          <button
                            onClick={() => openAddRole(emp, 'driver')}
                            className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 border border-indigo-200 bg-white text-xs text-indigo-700 hover:bg-indigo-50 transition"
                          >
                            <PlusIcon className="h-3.5 w-3.5" />
                            Driver Role
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-xl border border-stone-200 overflow-hidden">
            <div className="h-1 w-full bg-[#ee2b74]" />
            <div className="px-5 py-4 border-b border-stone-200 flex items-center justify-between">
              <h3 className="font-semibold text-stone-900 text-lg">Edit Employee</h3>
              <button onClick={() => setEditing(null)} className="text-stone-400 hover:text-stone-600 text-xl leading-none">✕</button>
            </div>
            <form onSubmit={submitEdit} className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1.5">First Name</label>
                  <input
                    required
                    className="w-full rounded-lg border border-stone-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#ee2b74] outline-none"
                    value={editForm.first_name}
                    onChange={(e) => setEditForm(f => ({ ...f, first_name: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1.5">Last Name</label>
                  <input
                    required
                    className="w-full rounded-lg border border-stone-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#ee2b74] outline-none"
                    value={editForm.last_name}
                    onChange={(e) => setEditForm(f => ({ ...f, last_name: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1.5">Cellphone Number</label>
                <input
                  className="w-full rounded-lg border border-stone-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#ee2b74] outline-none"
                  value={editForm.cellphone}
                  onChange={(e) => setEditForm(f => ({ ...f, cellphone: e.target.value }))}
                />
              </div>
              <p className="text-xs text-stone-400">
                Name and cellphone are shared across all roles for this person.
              </p>
              <div className="flex justify-end gap-2 pt-2 border-t border-stone-100">
                <button type="button" onClick={() => setEditing(null)} disabled={submitting}
                  className="rounded-lg border border-stone-300 px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 disabled:opacity-50">
                  Cancel
                </button>
                <button type="submit" disabled={submitting}
                  className="rounded-lg bg-[#ee2b74] hover:bg-[#d4245f] text-white px-4 py-2 text-sm font-medium disabled:opacity-50 transition">
                  {submitting ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add role modal */}
      {addingRoleFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-xl border border-stone-200 overflow-hidden">
            <div className={`h-1 w-full ${addingRoleFor.role === 'picker' ? 'bg-emerald-500' : 'bg-indigo-500'}`} />
            <div className="px-5 py-4 border-b border-stone-200 flex items-center justify-between">
              <h3 className="font-semibold text-stone-900 text-lg capitalize">
                Add {addingRoleFor.role} Role — {addingRoleFor.employee.first_name} {addingRoleFor.employee.last_name}
              </h3>
              <button onClick={() => setAddingRoleFor(null)} className="text-stone-400 hover:text-stone-600 text-xl leading-none">✕</button>
            </div>
            <form onSubmit={submitAddRole} className="p-5 space-y-4">
              {addRoleError && (
                <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                  {addRoleError}
                </div>
              )}
              <p className="text-xs text-stone-500">
                This uses their existing login — no new account is created.
              </p>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1.5">Store Assignment</label>
                <select
                  required
                  className="w-full rounded-lg border border-stone-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#ee2b74] outline-none"
                  value={addRoleForm.store_id}
                  onChange={(e) => setAddRoleForm(f => ({ ...f, store_id: e.target.value }))}
                >
                  <option value="">— Select store —</option>
                  {stores.map((store) => (
                    <option key={store.id} value={store.id}>{store.name}</option>
                  ))}
                </select>
              </div>
              {addingRoleFor.role === 'driver' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1.5">Car Model</label>
                    <input
                      required
                      placeholder="e.g. Toyota Corolla"
                      className="w-full rounded-lg border border-stone-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#ee2b74] outline-none"
                      value={addRoleForm.car_model}
                      onChange={(e) => setAddRoleForm(f => ({ ...f, car_model: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1.5">Number Plate</label>
                    <input
                      required
                      placeholder="e.g. ABC 123 GP"
                      className="w-full rounded-lg border border-stone-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#ee2b74] outline-none"
                      value={addRoleForm.number_plate}
                      onChange={(e) => setAddRoleForm(f => ({ ...f, number_plate: e.target.value }))}
                    />
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2 border-t border-stone-100">
                <button type="button" onClick={() => setAddingRoleFor(null)} disabled={submitting}
                  className="rounded-lg border border-stone-300 px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 disabled:opacity-50">
                  Cancel
                </button>
                <button type="submit" disabled={submitting}
                  className="rounded-lg bg-[#ee2b74] hover:bg-[#d4245f] text-white px-4 py-2 text-sm font-medium disabled:opacity-50 transition">
                  {submitting ? 'Adding…' : `Add ${addingRoleFor.role} Role`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Remove role modal */}
      {removingRoleFor && (() => {
        const isLastRole = removingRoleFor.employee.roles.length === 1;
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-4">
            <div className="w-full max-w-sm rounded-2xl bg-white shadow-xl border border-stone-200 overflow-hidden">
              <div className="h-1 w-full bg-red-500" />
              <div className="px-5 py-4 border-b border-stone-200 flex items-center justify-between">
                <h3 className="font-semibold text-stone-900 text-lg capitalize">Remove {removingRoleFor.role} Role</h3>
                <button onClick={() => setRemovingRoleFor(null)} className="text-stone-400 hover:text-stone-600 text-xl leading-none">✕</button>
              </div>
              <div className="p-5 space-y-4">
                {removeRoleError && (
                  <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                    {removeRoleError}
                  </div>
                )}
                <p className="text-sm text-stone-600">
                  Remove the <span className="font-semibold capitalize">{removingRoleFor.role}</span> role from{' '}
                  <span className="font-semibold text-stone-900">
                    {removingRoleFor.employee.first_name} {removingRoleFor.employee.last_name}
                  </span>?
                </p>
                {isLastRole ? (
                  <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5 text-sm text-amber-800">
                    <strong>This is their only role.</strong> Removing it means they will no longer be able to
                    log in to the Driver or Picker app at all. Their login itself is not deleted — you can add
                    a role back to it later from this page.
                  </div>
                ) : (
                  <p className="text-xs text-stone-400">
                    They will keep their other role(s) and can still log in normally.
                  </p>
                )}
                <div className="flex justify-end gap-2">
                  <button type="button" onClick={() => setRemovingRoleFor(null)} disabled={submitting}
                    className="rounded-lg border border-stone-300 px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 disabled:opacity-50">
                    Cancel
                  </button>
                  <button type="button" onClick={confirmRemoveRole} disabled={submitting}
                    className="rounded-lg bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm font-medium disabled:opacity-50 transition">
                    {submitting ? 'Removing…' : isLastRole ? 'Yes, Remove Anyway' : 'Remove Role'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
